import * as React from "react";
import { Box, Typography } from "@material-ui/core";

interface ICopyState {
  [key: string]: boolean;
}

interface IJsonRenderComponentProps {
  jsonObject: any;
  printHtml?: boolean;
}

const JsonRenderComponent: React.FC<IJsonRenderComponentProps> = (props) => {
  const [copiedStates, setCopiedStates] = React.useState<ICopyState>({});
  const [hoveredValue, setHoveredValue] = React.useState<string | null>(null);

  const handleCopy = async (key: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);

      setCopiedStates((prev: ICopyState) => ({
        ...prev,
        [key]: true,
      }));

      setTimeout(() => {
        setCopiedStates((prev: ICopyState) => ({
          ...prev,
          [key]: false,
        }));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const isCopied = (key: string): boolean => {
    return Boolean(copiedStates[key]);
  };

  const renderCopyButton = (key: string, value: string): JSX.Element => {
    const copied = isCopied(key);
    const isHovered = hoveredValue === key;

    return (
      <button
        onClick={() => handleCopy(key, value)}
        onMouseEnter={() => setHoveredValue(key)}
        onMouseLeave={() => setHoveredValue(null)}
        style={{
          alignItems: "center",
          backgroundColor: copied
            ? "#4CAF50"
            : isHovered
            ? "#f5f5f5"
            : "transparent",
          border: "none",
          borderRadius: "4px",
          color: copied ? "#ffffff" : "#757575",
          cursor: "pointer",
          display: "inline-flex",
          fontSize: "14px",
          fontWeight: "normal",
          marginLeft: "8px",
          outline: "none",
          padding: "6px 10px",
          transition: "all 0.2s ease",
        }}
        title={copied ? "Copied!" : "Click to copy"}
      >
        {copied ? (
          <span style={{ fontSize: "20px" }}>✓</span>
        ) : (
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
            <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z" />
            <path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z" />
          </svg>
        )}
      </button>
    );
  };

  const renderValue = (key: string, value: string): JSX.Element => {
    const isHovered = hoveredValue === key;

    return (
      <div
        style={{
          alignItems: "center",
          backgroundColor: isHovered ? "#f8f9fa" : "transparent",
          borderRadius: "4px",
          display: "flex",
          padding: "4px 8px",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={() => setHoveredValue(key)}
        onMouseLeave={() => setHoveredValue(null)}
      >
        <span
          style={{
            color: isHovered ? "#1976d2" : "inherit",
            fontSize: "14px",
            transition: "color 0.2s ease",
          }}
        >
          {value}
        </span>
        {renderCopyButton(key, value)}
      </div>
    );
  };

  function printArray(data: any[], printHtml: boolean): JSX.Element[] {
    return data.map((attribute: any, index: number) => (
      <div key={index}>
        <div>
          {typeof attribute === "object"
            ? printJSONObject(attribute, printHtml)
            : attribute}
        </div>
      </div>
    ));
  }

  function printObject(
    data: Record<string, any>,
    printHtml: boolean
  ): JSX.Element[] {
    return Object.entries(data)
      .filter(
        ([_, attributeValue]) => attributeValue != null && attributeValue !== ""
      )
      .map(([attributeKey, attributeValue]) => {
        const cleanHtml =
          typeof attributeValue === "object"
            ? printJSONObject(attributeValue, printHtml)
            : attributeValue;

        return (
          <div
            key={attributeKey}
            style={{
              paddingBottom: "5px",
            }}
          >
            <Box
              boxShadow={hoveredValue === attributeKey ? 4 : 3}
              bgcolor="background.paper"
              m={1}
              p={1}
              style={{
                transform:
                  hoveredValue === attributeKey
                    ? "translateY(-2px)"
                    : "translateY(0)",
                transition: "all 0.2s ease",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                style={{
                  color: hoveredValue === attributeKey ? "#1976d2" : "inherit",
                  paddingBottom: "5px",
                  transition: "color 0.2s ease",
                }}
              >
                {attributeKey}
              </Typography>

              <div
                style={{
                  marginLeft: "20px",
                }}
              >
                {typeof attributeValue !== "object"
                  ? renderValue(attributeKey, String(attributeValue))
                  : cleanHtml}
              </div>
            </Box>
          </div>
        );
      });
  }

  const printJSONObject = (
    data: any,
    printHtml: boolean
  ): JSX.Element | JSX.Element[] =>
    Array.isArray(data)
      ? printArray(data, printHtml)
      : printObject(data, printHtml);

  const handleCopyAll = async (): Promise<void> => {
    try {
      const filterObject = (obj: any): any => {
        if (Array.isArray(obj)) {
          return obj
            .filter((item) => item != null && item !== "")
            .map((item) =>
              typeof item === "object" ? filterObject(item) : item
            );
        }

        if (obj !== null && typeof obj === "object") {
          const filteredEntries = Object.entries(obj)
            .filter(([_, value]) => value != null && value !== "")
            .map(([key, value]) => [
              key,
              typeof value === "object" ? filterObject(value) : value,
            ]);

          return filteredEntries.reduce(
            (acc, [key, value]) => ({
              ...acc,
              [key as string]: value,
            }),
            {}
          );
        }

        return obj;
      };

      const filteredObject = filterObject(props.jsonObject);
      const jsonString = JSON.stringify(filteredObject, null, 2);
      await navigator.clipboard.writeText(jsonString);

      setCopiedStates((prev: ICopyState) => ({
        ...prev,
        copyAll: true,
      }));

      setTimeout(() => {
        setCopiedStates((prev: ICopyState) => ({
          ...prev,
          copyAll: false,
        }));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <button
          onClick={handleCopyAll}
          onMouseEnter={() => setHoveredValue("copyAll")}
          onMouseLeave={() => setHoveredValue(null)}
          style={{
            alignItems: "center",
            backgroundColor: isCopied("copyAll")
              ? "#4CAF50"
              : hoveredValue === "copyAll"
              ? "#f5f5f5"
              : "#ffffff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow:
              hoveredValue === "copyAll" ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
            color: isCopied("copyAll") ? "#ffffff" : "#000000",
            cursor: "pointer",
            display: "inline-flex",
            fontSize: "14px",
            outline: "none",
            padding: "8px 16px",
            transform:
              hoveredValue === "copyAll" ? "translateY(-1px)" : "translateY(0)",
            transition: "all 0.2s ease",
          }}
          title={isCopied("copyAll") ? "Copied!" : "Copy all content"}
        >
          {isCopied("copyAll") ? "✓ Copied All Content!" : "Copy All Content"}
        </button>
      </div>
      {printJSONObject(props.jsonObject, Boolean(props.printHtml))}
    </div>
  );
};

export default JsonRenderComponent;
